/**
 * OIMS v2 - Notifications module
 * Real-time health alerts via SSE with RBAC filtering.
 *
 * Security:
 * - C-1: All server content rendered through escapeHtml()
 * - C-2: Uses short-lived connection token (not JWT in URL)
 * - H-4: Uses sessionStorage (cleared on logout), not localStorage
 * - H-5: Aggregation buffer capped at 50 events
 */

import { escapeHtml } from './utils.js';

let eventSource = null;
let reconnectTimer = null;
let reconnectDelay = 1000;
const MAX_RECONNECT_DELAY = 10000;
const MAX_NOTIFICATIONS = 50;
const HEARTBEAT_TIMEOUT = 30000; // 30s (must be > 2x server heartbeat of 15s)
const WATCHDOG_INTERVAL = 20000; // 20s - reconnect if no active SSE
const EXPIRY_READ = 3600;    // 1 hour after viewing
const EXPIRY_UNREAD = 86400; // 24 hours if never viewed
let heartbeatTimer = null;
let expiryTimer = null;
let watchdogTimer = null;
let catchUpTimer = null;
const CATCHUP_INTERVAL = 15000; // 15s - poll for missed events (tunnel reliability)
let state = null;
let unreadCount = 0;
let notifications = [];
let swRegistration = null;
let pushSubscription = null;

// DOM refs
let bellBtn = null;
let badge = null;
let dropdown = null;
let notifList = null;
let notifEmpty = null;

const STATUS_ICONS = {
  unhealthy: '<i class="bi bi-x-circle-fill text-danger"></i>',
  degraded: '<i class="bi bi-exclamation-triangle-fill text-warning"></i>',
  healthy: '<i class="bi bi-check-circle-fill text-success"></i>',
};

const ACTION_ICONS = {
  sent: '<i class="bi bi-person-gear text-info"></i>',
  completed: '<i class="bi bi-check-circle-fill text-success"></i>',
  failed: '<i class="bi bi-x-circle-fill text-danger"></i>',
};

/**
 * Initialize the notification system after auth succeeds.
 */
export function initNotifications(appState) {
  state = appState;

  // Cache DOM refs
  bellBtn = document.getElementById('notificationBell');
  badge = document.getElementById('notifBadge');
  dropdown = document.getElementById('notifDropdown');
  notifList = document.getElementById('notifList');
  notifEmpty = document.querySelector('.notification-empty');

  if (!bellBtn) {
    console.warn('[Notifications] Bell element not found, aborting');
    return;
  }

  // Load cached notifications from sessionStorage (H-4)
  _loadFromSession();
  _purgeExpired();

  // Purge expired notifications every 60s
  expiryTimer = setInterval(_purgeExpired, 60000);

  // Toggle dropdown
  bellBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    dropdown.classList.toggle('show');
    if (dropdown.classList.contains('show')) {
      _markAllRead();
    }
  });

  // Close dropdown on outside click
  document.addEventListener('click', (e) => {
    if (dropdown && !bellBtn.contains(e.target)) {
      dropdown.classList.remove('show');
    }
  });

  // Connect to SSE
  _connect();

  // Watchdog: reconnect if SSE drops without onerror firing (proxy/tunnel issue)
  watchdogTimer = setInterval(() => {
    const dead = !eventSource || eventSource.readyState === EventSource.CLOSED;
    if (dead && !reconnectTimer) {
      console.info('[Notifications] Watchdog: SSE dead, reconnecting...');
      if (eventSource) { eventSource.close(); eventSource = null; }
      reconnectDelay = 1000;
      _connect();
    }
  }, WATCHDOG_INTERVAL);

  // Fetch catch-up events (initial + periodic poll for tunnel reliability)
  _fetchCatchUp();
  catchUpTimer = setInterval(_fetchCatchUp, CATCHUP_INTERVAL);
}

/**
 * Clean up SSE connection and event listeners on logout.
 */
export function cleanupNotifications() {
  if (eventSource) {
    eventSource.close();
    eventSource = null;
  }
  if (reconnectTimer) {
    clearTimeout(reconnectTimer);
    reconnectTimer = null;
  }
  if (heartbeatTimer) {
    clearTimeout(heartbeatTimer);
    heartbeatTimer = null;
  }
  if (expiryTimer) {
    clearInterval(expiryTimer);
    expiryTimer = null;
  }
  if (watchdogTimer) {
    clearInterval(watchdogTimer);
    watchdogTimer = null;
  }
  if (catchUpTimer) {
    clearInterval(catchUpTimer);
    catchUpTimer = null;
  }
  // H-4: Clear sessionStorage notification cache
  sessionStorage.removeItem('oims_notifications');
  notifications = [];
  unreadCount = 0;
  _updateBadge();
}

// ── Service Worker & Push Subscription ──────────────────────────

/**
 * Register the service worker and set up push subscriptions.
 */
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.warn('[Push] Service workers not supported');
    return;
  }

  try {
    swRegistration = await navigator.serviceWorker.register('/static/service-worker.js');
    console.info('[Push] Service worker registered');

    // Check existing push subscription
    pushSubscription = await swRegistration.pushManager.getSubscription();
    if (pushSubscription) {
      console.info('[Push] Existing push subscription found');
    }
  } catch (err) {
    console.error('[Push] Service worker registration failed:', err);
  }
}

/**
 * Subscribe to push notifications (requests permission if needed).
 */
export async function subscribeToPush() {
  if (!swRegistration) {
    console.warn('[Push] Service worker not registered');
    return false;
  }

  // Get VAPID public key from server
  let vapidKey;
  try {
    const { data } = await axios.get('/notifications/push/vapid-key');
    vapidKey = data.vapid_public_key;
  } catch (err) {
    console.warn('[Push] Push not configured on server');
    return false;
  }

  // Request notification permission
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    console.warn('[Push] Notification permission denied');
    return false;
  }

  try {
    // Convert VAPID key from base64url to Uint8Array
    const applicationServerKey = _urlBase64ToUint8Array(vapidKey);

    pushSubscription = await swRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey,
    });

    // Send subscription to server
    const sub = pushSubscription.toJSON();
    await axios.post('/notifications/push/subscribe', {
      endpoint: sub.endpoint,
      keys: sub.keys,
      device_name: _getDeviceName(),
    });

    // Store JWT in IndexedDB so service worker can make API calls
    await _storeTokenForServiceWorker();

    console.info('[Push] Push subscription activated');
    if (window.toast) window.toast.success('Push notifications enabled');
    return true;
  } catch (err) {
    console.error('[Push] Push subscribe failed:', err);
    if (window.toast) window.toast.error('Failed to enable push notifications');
    return false;
  }
}

/**
 * Unsubscribe from push notifications.
 */
export async function unsubscribeFromPush() {
  if (!pushSubscription) return false;

  try {
    const endpoint = pushSubscription.endpoint;
    await pushSubscription.unsubscribe();
    pushSubscription = null;

    // Remove from server
    await axios.delete('/notifications/push/subscribe', {
      data: { endpoint },
    });

    console.info('[Push] Push subscription removed');
    if (window.toast) window.toast.info('Push notifications disabled');
    return true;
  } catch (err) {
    console.error('[Push] Push unsubscribe failed:', err);
    return false;
  }
}

/**
 * Check if push is currently subscribed.
 */
export function isPushSubscribed() {
  return pushSubscription !== null;
}

/**
 * Store JWT in IndexedDB for service worker API calls.
 */
async function _storeTokenForServiceWorker() {
  if (!state || !state.accessToken) return;
  try {
    const db = await _openIDB();
    const tx = db.transaction('tokens', 'readwrite');
    tx.objectStore('tokens').put(state.accessToken, 'access_token');
  } catch (err) {
    console.warn('[Push] Failed to store token in IndexedDB:', err);
  }
}

function _openIDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open('oims-sw', 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('tokens')) {
        db.createObjectStore('tokens');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function _urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function _getDeviceName() {
  const ua = navigator.userAgent;
  if (/iPhone|iPad/.test(ua)) return 'iOS';
  if (/Android/.test(ua)) return 'Android';
  if (/Mac/.test(ua)) return 'macOS';
  if (/Win/.test(ua)) return 'Windows';
  if (/Linux/.test(ua)) return 'Linux';
  return 'Unknown';
}

/**
 * Connect to SSE endpoint using short-lived connection token.
 */
async function _connect() {
  if (!state || !state.accessToken) {
    console.warn('[Notifications] No access token, skipping SSE connect');
    return;
  }

  try {
    // C-2: Exchange JWT for short-lived connection token
    const { data } = await axios.post('/notifications/token');
    const token = data.token;

    // Connect SSE with connection token (not JWT)
    eventSource = new EventSource(`/notifications/stream?token=${encodeURIComponent(token)}`);

    // Start heartbeat timer immediately — if 'connected' event never arrives
    // (proxy/tunnel swallows it), this ensures we detect the zombie connection.
    _resetHeartbeat();

    eventSource.addEventListener('connected', () => {
      reconnectDelay = 1000; // Reset backoff on success
      _resetHeartbeat();
    });

    eventSource.addEventListener('notification', (e) => {
      _resetHeartbeat();
      try {
        const event = JSON.parse(e.data);
        // Dedup: skip if already received via catch-up
        if (_isDuplicate(event)) return;
        _handleNotification(event);
      } catch (err) {
        console.error('[Notifications] Failed to parse:', err);
      }
    });

    eventSource.addEventListener('heartbeat', () => {
      _resetHeartbeat();
    });

    eventSource.addEventListener('expired', () => {
      eventSource.close();
      eventSource = null;
      _scheduleReconnect();
    });

    eventSource.onerror = () => {
      eventSource.close();
      eventSource = null;
      _scheduleReconnect();
    };

  } catch (err) {
    _scheduleReconnect();
  }
}

function _scheduleReconnect() {
  if (reconnectTimer) return;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY);
    _connect();
  }, reconnectDelay);
}

function _resetHeartbeat() {
  if (heartbeatTimer) clearTimeout(heartbeatTimer);
  heartbeatTimer = setTimeout(() => {
    // No heartbeat for 45s, force reconnect
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    _connect();
  }, HEARTBEAT_TIMEOUT);
}

function _isDuplicate(event) {
  return notifications.some(n => {
    if (event.type === 'approval_update' && event.request_id) {
      return n.request_id === event.request_id && n.status === event.status;
    }
    return n.type === event.type &&
      n.instance_name === event.instance_name &&
      Math.abs(n.timestamp - (event.timestamp || 0)) < 2;
  });
}

function _handleNotification(event) {
  const notification = {
    id: Date.now() + Math.random(),
    type: event.type,
    group_name: event.group_name || '',
    instance_name: event.instance_name || '',
    old_status: event.old_status || '',
    new_status: event.new_status || '',
    details: event.details || '',
    timestamp: event.timestamp || Date.now() / 1000,
    read: false,
    // User action fields
    user: event.user || '',
    action_label: event.action_label || '',
    action_id: event.action_id || '',
    status: event.status || 'sent',
    // Approval fields (for dedup)
    request_id: event.request_id || '',
    requester: event.requester || '',
    approver: event.approver || '',
    // Schedule fields
    event: event.event || '',
    reason: event.reason || '',
    auto_stop_display: event.auto_stop_display || '',
  };

  // H-5: Cap at MAX_NOTIFICATIONS
  notifications.unshift(notification);
  if (notifications.length > MAX_NOTIFICATIONS) {
    notifications = notifications.slice(0, MAX_NOTIFICATIONS);
  }

  unreadCount++;
  _updateBadge();
  _renderNotifications();
  _saveToSession();
  _showBrowserNotification(notification);
  _animateBell();

  // Dispatch DOM event so dashboard Live Activity updates in real-time
  document.dispatchEvent(new CustomEvent('oims:notification', { detail: notification }));

  // Route approval_update events to the approvals module
  if (notification.type === 'approval_update') {
    document.dispatchEvent(new CustomEvent('oims:approval_update', { detail: notification }));
  }

  // Route schedule_update events to the AWS module
  if (notification.type === 'schedule_update') {
    document.dispatchEvent(new CustomEvent('oims:schedule_update', { detail: notification }));
  }
}

function _renderNotifications() {
  if (!notifList) return;

  if (notifications.length === 0) {
    notifList.innerHTML = '';
    if (notifEmpty) notifEmpty.style.display = 'block';
    return;
  }

  if (notifEmpty) notifEmpty.style.display = 'none';

  // C-1: All server content through escapeHtml()
  notifList.innerHTML = notifications.map(n => {
    const time = _formatTime(n.timestamp);
    const readClass = n.read ? 'read' : 'unread';

    if (n.type === 'approval_update') {
      const approvalIcons = {
        pending: '<i class="bi bi-shield-lock-fill text-warning"></i>',
        executing: '<i class="bi bi-arrow-repeat text-info"></i>',
        completed: '<i class="bi bi-check-circle-fill text-success"></i>',
        failed: '<i class="bi bi-exclamation-triangle-fill text-danger"></i>',
        denied: '<i class="bi bi-shield-x text-danger"></i>',
        expired: '<i class="bi bi-clock-history text-muted"></i>',
        cancelled: '<i class="bi bi-dash-circle text-muted"></i>',
      };
      const icon = approvalIcons[n.status] || '<i class="bi bi-shield-lock text-warning"></i>';
      const requester = n.requester || '';
      const approver = n.approver || '';
      let title = `${escapeHtml(n.action_label || '')} - ${escapeHtml(n.status || '')}`;
      let detail = escapeHtml(n.instance_name || '');
      if (approver && n.status !== 'pending') {
        detail += ` \u00b7 by ${escapeHtml(approver)}`;
      }
      if (requester) {
        detail += ` \u00b7 req: ${escapeHtml(requester)}`;
      }
      return `<div class="notification-item ${readClass}">
        <div class="notification-icon">${icon}</div>
        <div class="notification-content">
          <div class="notification-title">${title}</div>
          <div class="notification-detail">${detail}</div>
          <div class="notification-time">${escapeHtml(time)}</div>
        </div>
      </div>`;
    }

    if (n.type === 'user_action') {
      const userName = n.user || 'Unknown';
      const icon = ACTION_ICONS[n.status] || ACTION_ICONS.sent;
      const statusSuffix = n.status === 'completed' ? ' \u2713' : n.status === 'failed' ? ' \u2717' : '';
      return `<div class="notification-item ${readClass}">
        <div class="notification-icon">${icon}</div>
        <div class="notification-content">
          <div class="notification-title">${escapeHtml(userName)} \u2192 ${escapeHtml(n.action_label)}${statusSuffix}</div>
          <div class="notification-detail">${escapeHtml(n.instance_name)}</div>
          <div class="notification-time">${escapeHtml(time)}</div>
        </div>
      </div>`;
    }

    if (n.type === 'schedule_update') {
      const schedIcons = {
        started: '<i class="bi bi-play-circle-fill text-success"></i>',
        stopped: '<i class="bi bi-stop-circle-fill text-secondary"></i>',
        warning: '<i class="bi bi-exclamation-triangle-fill text-warning"></i>',
        failed: '<i class="bi bi-exclamation-circle-fill text-danger"></i>',
        extended: '<i class="bi bi-plus-circle-fill text-info"></i>',
      };
      const icon = schedIcons[n.event] || '<i class="bi bi-calendar-event text-info"></i>';
      const eventLabel = {
        started: 'Started',
        stopped: 'Stopped',
        warning: 'Stopping soon',
        failed: 'Failed',
        extended: 'Extended',
      }[n.event] || n.event || '';
      const detail = n.reason && n.event === 'stopped' ? `Reason: ${escapeHtml(n.reason)}` : (n.auto_stop_display || '');
      return `<div class="notification-item ${readClass}">
        <div class="notification-icon">${icon}</div>
        <div class="notification-content">
          <div class="notification-title">${escapeHtml(n.instance_name)} — ${escapeHtml(eventLabel)}</div>
          <div class="notification-detail">${escapeHtml(detail)}</div>
          <div class="notification-time">${escapeHtml(time)}</div>
        </div>
      </div>`;
    }

    // Health alert notification
    const icon = STATUS_ICONS[n.new_status] || '<i class="bi bi-info-circle"></i>';
    const isRecovery = n.new_status === 'healthy';
    const action = isRecovery ? 'recovered' : n.new_status;

    return `<div class="notification-item ${readClass}">
      <div class="notification-icon">${icon}</div>
      <div class="notification-content">
        <div class="notification-title">${escapeHtml(n.instance_name)} ${escapeHtml(action)}</div>
        <div class="notification-detail">${escapeHtml(n.group_name)} &middot; ${escapeHtml(n.details || (n.old_status + ' \u2192 ' + n.new_status))}</div>
        <div class="notification-time">${escapeHtml(time)}</div>
      </div>
    </div>`;
  }).join('');
}

function _updateBadge() {
  if (!badge) return;
  if (unreadCount > 0) {
    badge.textContent = unreadCount > 99 ? '99+' : String(unreadCount);
    badge.style.display = 'flex';
  } else {
    badge.style.display = 'none';
  }
}

function _markAllRead() {
  const now = Date.now() / 1000;
  unreadCount = 0;
  notifications.forEach(n => {
    if (!n.read) {
      n.read = true;
      n.read_at = now;
    }
  });
  _updateBadge();
  _renderNotifications();
  _saveToSession();
}

function _formatTime(ts) {
  const date = new Date(ts * 1000);
  const now = new Date();
  const diff = (now - date) / 1000;

  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return date.toLocaleDateString();
}

function _showBrowserNotification(notification) {
  if (!('Notification' in window)) return;

  if (Notification.permission === 'default') {
    Notification.requestPermission();
    return;
  }

  if (Notification.permission !== 'granted') return;

  let title, body;

  if (notification.type === 'approval_update') {
    const statusLabel = notification.status || 'update';
    title = `OIMS: ${notification.action_label || 'Approval'} - ${statusLabel}`;
    body = notification.instance_name || '';
    if (notification.approver) {
      body += ` \u00b7 by ${notification.approver}`;
    }
  } else if (notification.type === 'user_action') {
    const userName = notification.user || 'Unknown';
    const suffix = notification.status === 'completed' ? ' \u2713' : notification.status === 'failed' ? ' \u2717' : '';
    title = `OIMS: ${userName} \u2192 ${notification.action_label}${suffix}`;
    body = notification.instance_name;
  } else {
    const isRecovery = notification.new_status === 'healthy';
    title = `OIMS: ${notification.instance_name} ${isRecovery ? 'Recovered' : notification.new_status}`;
    body = `${notification.group_name} - ${notification.details || notification.old_status + ' \u2192 ' + notification.new_status}`;
  }

  try {
    new Notification(title, {
      body: body,
      tag: `oims-${notification.instance_name}`,
      renotify: true,
    });
  } catch (e) {
    // Service worker required in some browsers, silently fail
  }
}

function _animateBell() {
  if (!bellBtn) return;
  bellBtn.classList.add('bell-ring');
  setTimeout(() => bellBtn.classList.remove('bell-ring'), 1000);
}

/**
 * Remove expired notifications:
 * - Read: expires 1 hour after read_at
 * - Unread: expires 24 hours after received (timestamp)
 */
function _purgeExpired() {
  const now = Date.now() / 1000;
  const before = notifications.length;
  notifications = notifications.filter(n => {
    if (n.read && n.read_at) {
      return (now - n.read_at) < EXPIRY_READ;
    }
    return (now - n.timestamp) < EXPIRY_UNREAD;
  });
  if (notifications.length !== before) {
    unreadCount = notifications.filter(n => !n.read).length;
    _updateBadge();
    _renderNotifications();
    _saveToSession();
  }
}

// H-4: sessionStorage (not localStorage)
function _saveToSession() {
  try {
    sessionStorage.setItem('oims_notifications', JSON.stringify(notifications.slice(0, MAX_NOTIFICATIONS)));
  } catch (e) {
    // Storage full, ignore
  }
}

function _loadFromSession() {
  try {
    const cached = sessionStorage.getItem('oims_notifications');
    if (cached) {
      notifications = JSON.parse(cached).slice(0, MAX_NOTIFICATIONS);
      unreadCount = notifications.filter(n => !n.read).length;
      _updateBadge();
      _renderNotifications();
    }
  } catch (e) {
    notifications = [];
  }
}

async function _fetchCatchUp() {
  if (!state || !state.accessToken) return;
  try {
    const { data } = await axios.get('/notifications/catch-up');
    const events = data.events || [];
    // Add catch-up events that we don't already have
    for (const event of events) {
      const exists = notifications.some(n => {
        // Approval events: dedup by request_id + status (unique per state change)
        if (event.type === 'approval_update' && event.request_id) {
          return n.request_id === event.request_id && n.status === event.status;
        }
        // Other events: dedup by type + instance_name + timestamp
        return n.type === event.type &&
          n.instance_name === event.instance_name &&
          Math.abs(n.timestamp - (event.timestamp || 0)) < 2;
      });
      if (!exists) {
        _handleNotification(event);
      }
    }
  } catch (e) {
    // Catch-up is best-effort
    console.debug('Notification catch-up failed:', e);
  }
}
